import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import queue from '@nullvoxpopuli/ember-composable-helpers/helpers/queue';
import t from 'ember-intl/helpers/t';
import eq from 'ember-truth-helpers/helpers/eq';
import moment from 'moment-timezone';
import BvBadge from 'volta/components/bv-badge';
import BvBtnGroup from 'volta/components/bv-btn-group';
import BvDateField from 'volta/components/bv-date-field';
import BvIcon from 'volta/components/bv-icon';
import BvInline from 'volta/components/bv-inline';
import BvPopover from 'volta/components/bv-popover';
import BvRadio from 'volta/components/bv-radio';
import BvText from 'volta/components/bv-text';
import isPresent from 'volta/helpers/is-present';
import {
  DirectionsOptions,
  PeriodsOptions,
  DynamicCurrentDate,
  DynamicDatePrefix
} from 'volta/models/constants/date-filters';

import type { IntlService } from 'ember-intl';
import type { TDirectionOption, TPeriodOption } from 'volta/models/constants/date-filters';

interface DynamicDateFilterArgs {
  value?: string;
  valueDidChange: (isFromCalendar: boolean, newValue?: Date | string) => void;
  handleDynamicValue: (value: string) => void;
  dynamicValue?: string;
}

export default class DynamicDateFilter extends Component<DynamicDateFilterArgs> {
  <template>
    {{! @glint-nocheck: not typesafe yet }}
    <BvDateField
      @allowClear={{true}}
      @dataName={{@name}}
      @fullWidth={{true}}
      @iconClass={{if @dynamicValue 'u-color-blue'}}
      @matchTriggerWidth={{true}}
      @onChange={{fn @valueDidChange true}}
      @onClearButtonClick={{fn @valueDidChange true undefined}}
      @size='sm'
      @value={{this.value}}
      @error={{this.dynamicValueError}}
      class='u-flex-fill'
      data-test-date-filter={{@name}}
    >
      <:menu>
        <div>
          <BvInline @wrap={{false}} @blockAlign='center' class='u-px-3 u-py-1'>
            <BvRadio
              @disabled={{@disabled}}
              @onChange={{this.handleCheckDynamicValue}}
              @checked={{isPresent this.dynamicValue}}
            />
            <span class='bv-badge-group'>
              <BvPopover as |popover|>
                <BvBadge @onClick={{popover.toggle}} {{popover.trigger}}>
                  <BvText
                    @text={{if
                      this.dynamicValue
                      this.dynamicValue.direction.label
                      this.directions.[0].label
                    }}
                  />
                  <BvIcon @glyph='select' @size='sm' />
                </BvBadge>
                <popover.Content class='u-p-1'>
                  <BvBtnGroup @outline={{true}} @color='primary' @size='sm' as |g|>
                    {{#each this.directions key='key' as |direction|}}
                      <g.btn
                        @text={{direction.label}}
                        @onClick={{queue (fn this.selectDirection direction) popover.close}}
                        @pressed={{eq this.dynamicValue.direction.key direction.key}}
                      />
                    {{/each}}
                  </BvBtnGroup>
                </popover.Content>
              </BvPopover>
              {{! template-lint-disable require-input-label }}
              <input
                type='number'
                value='{{this.dynamicValue.count}}'
                style={{htmlSafe 'width: 50px; margin-left: -1px;'}}
                class='u-bg-surface u-border'
                min={{1}}
                max={{365}}
                placeholder={{1}}
                {{on 'change' this.changeCount}}
              />
              <BvPopover as |popover|>
                <BvBadge
                  @text={{this.dynamicValue.period.label}}
                  @onClick={{popover.toggle}}
                  {{popover.trigger}}
                >
                  <BvText
                    @text={{if
                      this.dynamicValue
                      this.dynamicValue.period.label
                      this.periods.[0].label
                    }}
                  />
                  <BvIcon @glyph='select' @size='sm' />
                </BvBadge>
                <popover.Content class='u-p-1'>
                  <BvBtnGroup @outline={{true}} @color='primary' @size='sm' as |g|>
                    {{#each this.periods as |period|}}
                      <g.btn
                        @text={{period.abv}}
                        @onClick={{queue (fn this.selectPeriod period) popover.close}}
                        @pressed={{eq this.dynamicValue.period period}}
                      />
                    {{/each}}
                  </BvBtnGroup>
                </popover.Content>
              </BvPopover>
            </span>
          </BvInline>
          <BvInline @wrap={{false}} @blockAlign='center' class='u-px-3 u-py-1 u-border-top-divider'>
            <BvRadio
              @disabled={{@disabled}}
              @onChange={{fn @handleDynamicValue this.dynamicCurrentDate}}
              @checked={{eq @dynamicValue this.dynamicCurrentDate}}
              @label={{component BvBadge text=(t 'currentDate')}}
            />
          </BvInline>
        </div>
      </:menu>
    </BvDateField>
  </template>
  @service intl!: IntlService;
  directions = DirectionsOptions(this.intl);
  periods = PeriodsOptions(this.intl);
  dynamicCurrentDate = DynamicCurrentDate;

  /**
   * Getters
   */

  get value() {
    if (moment.utc(this.args.value).isValid()) {
      return this.args.value;
    }

    const dynamicValue = this.dynamicValue;
    switch (this.args.value) {
      case DynamicCurrentDate:
        return moment.utc();
      default:
        if (dynamicValue) {
          if (dynamicValue.direction?.key === 'last') {
            return moment
              .utc()
              .subtract(
                dynamicValue.count,
                dynamicValue.period?.key as moment.unitOfTime.DurationConstructor
              );
          }
          return moment
            .utc()
            .add(
              dynamicValue.count,
              dynamicValue.period?.key as moment.unitOfTime.DurationConstructor
            );
        }
    }
    return undefined;
  }

  get dateFunction() {
    const dynamicValue = this.dynamicValue;
    return dynamicValue
      ? `${DynamicDatePrefix}${dynamicValue.direction?.key}_${dynamicValue.period?.key}_${dynamicValue.count}`
      : undefined;
  }

  get dynamicValue() {
    const { value } = this.args;
    if (value?.startsWith(DynamicDatePrefix)) {
      const [_prefix, direction, period, count] = value.split('_');
      const parsedCount = Number(count);
      if (count === '' || Number.isNaN(parsedCount)) {
        return undefined;
      }
      const parsedPeriod = this.periods.find((p) => p.key === period) ?? this.periods[0];
      const parsedDirection =
        this.directions.find((d) => d.key === direction) ?? this.directions[0];

      return { direction: parsedDirection, period: parsedPeriod, count: parsedCount };
    } else {
      return undefined;
    }
  }

  get dynamicValueError() {
    return this.args.value?.startsWith(DynamicDatePrefix) &&
      this.args.value != this.dynamicCurrentDate &&
      !this.dynamicValue
      ? this.intl.t('filterDefinitions.dynamicFilterError')
      : undefined;
  }

  /**
   * Actions
   */

  @action
  selectDirection(direction: TDirectionOption) {
    this.updateDynamicValue(this.buildDynamicValue(direction, undefined, undefined));
  }

  @action
  selectPeriod(period: TPeriodOption) {
    this.updateDynamicValue(this.buildDynamicValue(undefined, period, undefined));
  }

  @action
  changeCount(event: InputEvent) {
    const value = Number((event.target as HTMLInputElement).value);

    if (!Number.isNaN(value) && value > 0) {
      this.updateDynamicValue(this.buildDynamicValue(undefined, undefined, value));
    }
  }

  @action
  updateDynamicValue(dynamicValue: string) {
    this.args.handleDynamicValue(dynamicValue);
  }

  @action
  handleCheckDynamicValue() {
    this.args.handleDynamicValue(this.buildDynamicValue());
  }

  buildDynamicValue(direction?: TDirectionOption, period?: TPeriodOption, count?: number) {
    const {
      dynamicValue = {
        direction: this.directions[0],
        period: this.periods[0],
        count: 1
      }
    } = this;
    const dyn = {
      direction: direction?.key ?? dynamicValue.direction?.key,
      period: period?.key ?? dynamicValue.period?.key,
      count: count ?? dynamicValue.count
    };
    return `${DynamicDatePrefix}${dyn.direction}_${dyn.period}_${dyn.count}`;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Filters::DynamicDateFilter': typeof DynamicDateFilter;
    'filters/dynamic-date-filter': typeof DynamicDateFilter;
  }
}
